import type { ITenantDestination } from '@/entities/tenant-destination'
import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { IPOI, PoiListParams } from '@/shared/api/types/poi-types'
import type { ICountry, IProvince } from '@/shared/api/types/country-types'
import { useNavigate, useParams } from '@tanstack/react-router'
import { generatePath } from 'react-router'
import { ArrowLeft, Loader2, Save } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantPoisInfiniteQuery } from '@/entities/poi/api/poi-queries'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import {
  useTenantDestinationByIdQuery,
  useTenantDestinationsQuery,
} from '@/entities/tenant-destination'
import {
  buildTourDraft,
  hasTourDraftContent,
  useCreateTourMutation,
  useTourByIdQuery,
  useUpdateTourMutation,
} from '@/entities/tour'
import type {
  ITourPoi,
  ITourTranslation,
  PersistedTourDraft,
  UpdateTourPayload,
} from '@/entities/tour'
import {
  useAddTourPoisMutation,
  useRemoveTourPoisMutation,
  useUpdateTourPoiOrderMutation,
  useUpdateTourPoisBatchMutation,
} from '@/entities/tour/api/tour-queries'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'

import { TourBasicInfoForm } from '@/features/tour/tour-basic-info-form'
import { TourMapPreview } from '@/features/tour/tour-map-preview'
import { TourPoiOrderList } from '@/features/tour/tour-poi-order-list'
import { TourPoiSelector } from '@/features/tour/tour-poi-selector'
import {
  getPoiCoordinatesFromSelection,
  normalizeTourCoordinates,
} from '@/features/tour/tour-route-map-utils'

import { TourTranslationSection } from '@/features/tour/tour-translation-section'
import {
  DEFAULT_CSS_COLOR_NAME,
  DEFAULT_FETCH_LIMIT,
  ReasonType,
  TRANSLATION_JOB_SCOPE,
} from '@/shared/constants'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { cn } from '@/shared/libs/cn'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog'
import { Skeleton } from '@/shared/ui/skeleton'
import { ImageUploadField } from '@/shared/ui/image-upload-field'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { SectionHeader } from '@/shared/ui/section-header'
import { Switch } from '@/shared/ui/switch'
import { hasRequiredTranslations, resolveDefaultLanguages } from '@/shared/utils/language-utils'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'

interface TourBasicInfoState {
  duration: number
  scopeType: 'province' | 'tenantDestination'
  countryId: string
  provinceId: string
  tenantDestinationId: string
  ticketLink: string
  isFavorite: boolean
}
type EditSection = 'translations' | 'basicInfo' | 'coordinates' | 'media'

const DEFAULT_BASIC_INFO: TourBasicInfoState = {
  duration: 60,
  scopeType: 'tenantDestination',
  countryId: '',
  provinceId: '',
  tenantDestinationId: '',
  ticketLink: '',
  isFavorite: false,
}

function buildTourTranslationSnapshot(translations: ITourTranslation[]): string {
  return JSON.stringify(
    translations.map((item) => ({
      language: item.language,
      name: item.name.trim(),
      description: item.description.trim(),
    })),
  )
}

function buildTourBasicInfoSnapshot(basicInfo: TourBasicInfoState): string {
  return JSON.stringify({
    duration: basicInfo.duration,
    scopeType: basicInfo.scopeType,
    tenantDestinationId: basicInfo.tenantDestinationId,
    ticketLink: basicInfo.ticketLink,
    isFavorite: basicInfo.isFavorite,
  })
}

function buildTourMediaSnapshot(banner?: string, thumbnail?: string): string {
  return JSON.stringify({
    banner: banner ?? '',
    thumbnail: thumbnail ?? '',
  })
}

function buildTourCoordinatesSnapshot(coordinates: TourRouteCoordinate[]): string {
  return JSON.stringify(normalizeTourCoordinates(coordinates))
}
interface TourFormState {
  basicInfo: TourBasicInfoState
  translations: ITourTranslation[]
  selectedPois: ITourPoi[]
  routeCoordinates: TourRouteCoordinate[]
  thumbnail?: string
  banner?: string
}

export function TourFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const tenantId = params.tenantId ?? ''
  const { tourId } = params
  const isEdit = Boolean(tourId)

  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const { data: tenantResponse } = useTenantByIdQuery(tenantId)

  const { data: tourResponse, isLoading: isTourLoading } = useTourByIdQuery(tourId ?? '')
  const isFormReady = !isTourLoading && tenantLanguagesQuery.isFetched

  const createMutation = useCreateTourMutation()
  const updateMutation = useUpdateTourMutation()
  const addTourPoisMutation = useAddTourPoisMutation()
  const removeTourPoisMutation = useRemoveTourPoisMutation()
  const updateTourPoiOrderMutation = useUpdateTourPoiOrderMutation()
  const updateTourPoisBatchMutation = useUpdateTourPoisBatchMutation()

  const serverPoisRef = useRef<ITourPoi[]>([])
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null)
  const [isBannerUploading, setIsBannerUploading] = useState(false)
  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false)
  const [poiQueryFilters, setPoiQueryFilters] = useState<
    Pick<PoiListParams, 'keyword' | 'category' | 'type' | 'isActive'>
  >({})

  const [formState, setFormState] = useState<TourFormState>({
    basicInfo: DEFAULT_BASIC_INFO,
    translations: [],
    selectedPois: [],
    routeCoordinates: [],
  })
  const [isActive, setIsActive] = useState(true)
  const [savingSection, setSavingSection] = useState<EditSection | null>(null)
  const [, setHasPendingTranslationGeneration] = useState(false)
  const draftEntityId = useMemo(() => `draft:tour:${tenantId || 'unknown-tenant'}`, [tenantId])
  const initialTranslationsSnapshotRef = useRef(buildTourTranslationSnapshot([]))
  const initialBasicInfoSnapshotRef = useRef(buildTourBasicInfoSnapshot(DEFAULT_BASIC_INFO))
  const initialCoordinatesSnapshotRef = useRef(buildTourCoordinatesSnapshot([]))
  const initialMediaSnapshotRef = useRef(buildTourMediaSnapshot())
  const initialPoisSnapshotRef = useRef('')

  const { basicInfo, translations, selectedPois, routeCoordinates, thumbnail, banner } = formState
  const translationEntityId = isEdit && tourId ? tourId : draftEntityId
  const { pendingSuccessJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.TOUR,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
  })
  const tourDraft = useMemo(
    () =>
      buildTourDraft({
        basicInfo,
        translations,
        selectedPois,
        routeCoordinates,
        thumbnail,
        banner,
      }),
    [banner, basicInfo, routeCoordinates, selectedPois, thumbnail, translations],
  )
  const hasTourDraft = useMemo(
    () =>
      hasTourDraftContent(
        {
          basicInfo,
          translations,
          selectedPois,
          routeCoordinates,
          thumbnail,
          banner,
        },
        DEFAULT_BASIC_INFO,
      ),
    [banner, basicInfo, routeCoordinates, selectedPois, thumbnail, translations],
  )
  const hydrateTourDraft = useCallback((persistedDraft?: PersistedTourDraft) => {
    const nextState: TourFormState = {
      basicInfo: persistedDraft?.basicInfo ?? DEFAULT_BASIC_INFO,
      translations: persistedDraft?.translations ?? [],
      selectedPois: persistedDraft?.selectedPois ?? [],
      routeCoordinates: persistedDraft?.routeCoordinates ?? [],
      ...(persistedDraft?.thumbnail ? { thumbnail: persistedDraft.thumbnail } : {}),
      ...(persistedDraft?.banner ? { banner: persistedDraft.banner } : {}),
    }
    setFormState(nextState)
    setIsActive(true)
    initialTranslationsSnapshotRef.current = buildTourTranslationSnapshot([])
    initialBasicInfoSnapshotRef.current = buildTourBasicInfoSnapshot(DEFAULT_BASIC_INFO)
    initialCoordinatesSnapshotRef.current = buildTourCoordinatesSnapshot([])
    initialMediaSnapshotRef.current = buildTourMediaSnapshot()
    initialPoisSnapshotRef.current = ''
  }, [])
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.TOUR,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
    initSignature: `create:${tenantId}`,
    draft: tourDraft,
    hasContent: hasTourDraft,
    onHydrate: hydrateTourDraft,
  })

  const tenantLanguageItems = useMemo(
    () => tenantLanguagesQuery.items ?? [],
    [tenantLanguagesQuery.items],
  )
  const {
    selectedLanguageCode: selectedPoiLanguageCode,
    setSelectedLanguageCode: setSelectedPoiLanguageCode,
    languageOptions: poiLanguageOptions,
  } = useLanguageFilter(tenantLanguageItems)

  /** Ngôn ngữ thực sự dùng để fetch POI: fallback về default tenant rồi đến option đầu nếu state chưa kịp set. */
  const effectivePoiLanguageCode = useMemo(() => {
    if (selectedPoiLanguageCode) {
      return selectedPoiLanguageCode
    }
    return (
      poiLanguageOptions.find((item) => item.isDefault)?.value ?? poiLanguageOptions[0]?.value ?? ''
    )
  }, [poiLanguageOptions, selectedPoiLanguageCode])

  const { data: countriesResponse } = useCountriesQuery({})
  const { data: provincesResponse, isLoading: isProvincesLoading } = useProvincesQuery({
    countryId: basicInfo.countryId,
  })
  const { data: tenantDestinationsResponse, isLoading: isTenantDestinationsLoading } =
    useTenantDestinationsQuery({
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      isActive: true,
      ...(basicInfo.countryId ? { countryId: basicInfo.countryId } : {}),
      ...(basicInfo.provinceId ? { provinceId: basicInfo.provinceId } : {}),
      ...(effectivePoiLanguageCode ? { languageCode: effectivePoiLanguageCode } : {}),
    })

  const canFetchPois = Boolean(basicInfo.tenantDestinationId && effectivePoiLanguageCode)
  const poiQueryParams = useMemo(
    () => ({
      pageSize: 10,
      ...poiQueryFilters,
      ...(effectivePoiLanguageCode ? { languageCode: effectivePoiLanguageCode } : {}),
      ...(basicInfo.tenantDestinationId
        ? { tenantDestinationId: basicInfo.tenantDestinationId }
        : {}),
    }),
    [basicInfo.tenantDestinationId, effectivePoiLanguageCode, poiQueryFilters],
  )
  const {
    data: poisInfiniteData,
    isLoading: isPoisLoading,
    isFetching: isPoisFetching,
    isFetchingNextPage: isPoisFetchingNextPage,
    hasNextPage: poisHasNextPage,
    fetchNextPage: fetchNextPoisPage,
  } = useTenantPoisInfiniteQuery(tenantId, poiQueryParams, { enabled: canFetchPois })
  // * VI: Form tour chỉ lấy POI theo tenant + tenant_destination_id đã chọn để đảm bảo chọn đúng POI trong phạm vi đích đến của tenant. | EN: Tour form fetches POIs by tenant + selected tenant_destination_id to ensure POI selection stays within the tenant destination scope.

  const languages = useMemo(
    () => resolveDefaultLanguages(tenantLanguageItems),
    [tenantLanguageItems],
  )
  const tenantName = tenantResponse?.data?.name ?? tenantId
  const countryItems = useMemo<ICountry[]>(() => countriesResponse?.data ?? [], [countriesResponse])
  const provinceItems = useMemo<IProvince[]>(
    () => provincesResponse?.data ?? [],
    [provincesResponse],
  )
  const tenantDestinationItems = useMemo<ITenantDestination[]>(
    () => tenantDestinationsResponse?.data?.data ?? [],
    [tenantDestinationsResponse?.data?.data],
  )
  const countryOptions = useMemo(
    () =>
      countryItems.map((item) => ({
        value: item.id,
        label: item.nativeName ?? item.name ?? item.code,
      })),
    [countryItems],
  )
  const provinceOptions = useMemo(
    () =>
      provinceItems.map((item) => ({
        value: item.id,
        label: item.nativeName ?? item.name ?? item.code,
      })),
    [provinceItems],
  )
  const selectedTenantDestinationFromList = useMemo(
    () => tenantDestinationItems.find((item) => item.id === basicInfo.tenantDestinationId),
    [basicInfo.tenantDestinationId, tenantDestinationItems],
  )
  const { data: selectedTenantDestinationResponse } = useTenantDestinationByIdQuery(
    basicInfo.tenantDestinationId,
    effectivePoiLanguageCode || undefined,
    { enabled: !!effectivePoiLanguageCode },
  )
  const selectedTenantDestination =
    selectedTenantDestinationResponse?.data ?? selectedTenantDestinationFromList
  const resolvedTenantDestinationItems = useMemo(() => {
    if (!selectedTenantDestination) {
      return tenantDestinationItems
    }
    return tenantDestinationItems.some((item) => item.id === selectedTenantDestination.id)
      ? tenantDestinationItems
      : [selectedTenantDestination, ...tenantDestinationItems]
  }, [selectedTenantDestination, tenantDestinationItems])
  const tenantDestinationOptions = useMemo(
    () =>
      resolvedTenantDestinationItems.map((item) => ({
        value: item.id,
        label: item.localizedData?.[effectivePoiLanguageCode]?.name ?? 'No content available',
      })),
    [effectivePoiLanguageCode, resolvedTenantDestinationItems],
  )
  const pois = useMemo<IPOI[]>(
    () => poisInfiniteData?.pages.flatMap((page) => page.data) ?? [],
    [poisInfiniteData?.pages],
  )
  const poiMap = useMemo<Record<string, IPOI>>(() => {
    const map = Object.fromEntries(pois.map((poi) => [poi.id, poi]))
    selectedPois.forEach((item) => {
      if (item.poi && !map[item.poiId]) {
        map[item.poiId] = item.poi
      }
    })
    return map
  }, [pois, selectedPois])
  const outOfScopePoiIds = useMemo<Set<string>>(() => {
    const outliers = new Set<string>()

    selectedPois.forEach((item) => {
      const poi = poiMap[item.poiId] ?? item.poi
      const destinationId = poi?.tenantDestinationId

      if (!destinationId || destinationId !== basicInfo.tenantDestinationId) {
        outliers.add(item.poiId)
      }
    })
    return outliers
  }, [poiMap, selectedPois, basicInfo.tenantDestinationId])

  useLayoutEffect(() => {
    if (!isEdit || !tourResponse?.data) return
    // * VI: useLayoutEffect để hydrate form ngay trước khi paint, tránh flash frame trống khi dữ liệu tour đã có trong cache.
    // * EN: useLayoutEffect hydrates form before paint to prevent an empty-frame flash when tour data is already cached.
    // * VI: Khi mở chế độ edit, ưu tiên tenant_destination_id của chính tour; chỉ fallback suy luận từ POI khi backend chưa trả field này.
    // * EN: In edit mode, prefer tour.tenant_destination_id; only fall back to inferring from POIs when backend doesn't return this field.
    const tour = tourResponse.data
    const normalizedCoordinates = normalizeTourCoordinates(tour.coordinates)
    const tourPois = (tour.pois ?? []).slice().sort((a, b) => a.order - b.order)
    // * VI: Nếu tất cả POI cùng 1 tenant destination thì giữ scope theo destination; ngược lại fallback về province để tránh gán sai scope.
    // * EN: If all POIs belong to one tenant destination, keep destination scope; otherwise fall back to province scope to avoid incorrect scope assignment.
    const inferredTenantDestinationIds = Array.from(
      new Set(
        tourPois
          .map((item) => item.poi?.tenantDestinationId)
          .filter((item): item is string => Boolean(item)),
      ),
    )
    const resolvedTenantDestinationId =
      tour.tenantDestinationId ??
      (inferredTenantDestinationIds.length === 1 ? (inferredTenantDestinationIds[0] ?? '') : '')
    const inferredScopeType: TourBasicInfoState['scopeType'] = resolvedTenantDestinationId
      ? 'tenantDestination'
      : 'province'
    const inferredTenantDestinationId = resolvedTenantDestinationId
    const nextBasicInfo: TourBasicInfoState = {
      duration: tour.duration ?? 60,
      scopeType: inferredScopeType,
      countryId: '',
      provinceId: '',
      tenantDestinationId: inferredTenantDestinationId,
      ticketLink: tour.ticketLink ?? '',
      isFavorite: tour.isFavorite ?? false,
    }

    setFormState(
      (prev): TourFormState => ({
        ...prev,
        basicInfo: nextBasicInfo,
        routeCoordinates: normalizedCoordinates,
        translations: tour.localizedData,
        selectedPois: tourPois,
        ...(tour.thumbnail ? { thumbnail: tour.thumbnail } : {}),
        ...(tour.banner ? { banner: tour.banner } : {}),
      }),
    )
    // * VI: Cập nhật snapshot gốc ngay sau hydrate để so sánh dirty-state chính xác, tránh báo "đã chỉnh sửa" sai ngay khi vừa mở form edit.
    // * EN: Update initial snapshots right after hydration so dirty-state comparison is accurate and doesn't flag false edits on first load.
    initialTranslationsSnapshotRef.current = buildTourTranslationSnapshot(tour.localizedData)
    initialBasicInfoSnapshotRef.current = buildTourBasicInfoSnapshot(nextBasicInfo)
    initialCoordinatesSnapshotRef.current = buildTourCoordinatesSnapshot(normalizedCoordinates)
    initialMediaSnapshotRef.current = buildTourMediaSnapshot(tour.banner, tour.thumbnail)
    const poisSnapshot = JSON.stringify(
      tourPois
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((p) => ({
          poiId: p.poiId,
          color: p.color ?? null,
          favoritePoiRank: p.favoritePoiRank ?? null,
        })),
    )
    initialPoisSnapshotRef.current = poisSnapshot
    serverPoisRef.current = tourPois
  }, [isEdit, tourResponse?.data])

  useEffect(() => {
    // * VI: Khi user chọn tenant destination, effect này tự đồng bộ country/province theo destination đã chọn để tránh lệch bộ lọc địa lý.
    // * EN: When a tenant destination is selected, this effect auto-syncs country/province from that destination to keep geographic filters consistent.
    if (!basicInfo.tenantDestinationId) {
      return
    }
    const matchedDestination = resolvedTenantDestinationItems.find(
      (item) => item.id === basicInfo.tenantDestinationId,
    )
    if (!matchedDestination) {
      return
    }
    if (
      basicInfo.countryId === matchedDestination.countryId &&
      basicInfo.provinceId === matchedDestination.provinceId
    ) {
      return
    }
    setFormState((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        countryId: matchedDestination.countryId ?? '',
        provinceId: matchedDestination.provinceId ?? '',
      },
    }))
  }, [
    basicInfo.countryId,
    basicInfo.provinceId,
    basicInfo.tenantDestinationId,
    resolvedTenantDestinationItems,
  ])

  useEffect(() => {
    // * VI: Nếu chưa có province nhưng đã chọn POI, effect này suy luận country/province chung từ tập POI để giảm thao tác nhập tay.
    // * EN: If province is empty but POIs are selected, this effect infers common country/province from selected POIs to reduce manual input.
    if (basicInfo.provinceId || selectedPois.length === 0) {
      return
    }
    if (resolvedTenantDestinationItems.length === 0) {
      return
    }
    const selectedDestinationIds = Array.from(
      new Set(
        selectedPois
          .map((item) => item.poi?.tenantDestinationId)
          .filter((item): item is string => Boolean(item)),
      ),
    )
    if (selectedDestinationIds.length === 0) {
      return
    }
    const selectedDestinations = selectedDestinationIds
      .map((destinationId) =>
        resolvedTenantDestinationItems.find((item) => item.id === destinationId),
      )
      .filter((item): item is ITenantDestination => Boolean(item))
    if (selectedDestinations.length !== selectedDestinationIds.length) {
      return
    }
    const provinceIds = Array.from(new Set(selectedDestinations.map((item) => item.provinceId)))
    const countryIds = Array.from(new Set(selectedDestinations.map((item) => item.countryId)))
    if (provinceIds.length !== 1 || countryIds.length !== 1) {
      return
    }
    setFormState((prev) => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        countryId: countryIds[0] ?? '',
        provinceId: provinceIds[0] ?? '',
      },
    }))
  }, [basicInfo.provinceId, basicInfo.scopeType, selectedPois, resolvedTenantDestinationItems])

  // * VI: Chuẩn hóa dữ liệu dịch trước khi validate/lưu bằng cách trim name/description để tránh sai khác do khoảng trắng.
  // * EN: Normalize translations before validation/save by trimming name/description to avoid whitespace-only diffs.
  function buildNormalizedTranslations() {
    return translations.map((item) => ({
      ...item,
      name: item.name.trim(),
      description: item.description.trim(),
    }))
  }

  // * VI: Ưu tiên toạ độ user chỉnh trên map; nếu thiếu điểm thì fallback toạ độ suy ra từ POI đã chọn.
  // * EN: Prefer map-edited coordinates; if insufficient points, fall back to coordinates inferred from selected POIs.
  function buildCoordinatesForPayload() {
    const fromMap = normalizeTourCoordinates(routeCoordinates)
    const fromPoiSelection = normalizeTourCoordinates(
      getPoiCoordinatesFromSelection(selectedPois, poiMap),
    )
    return fromMap.length >= 2 ? fromMap : fromPoiSelection
  }

  // * VI: Kiểm tra điều kiện bắt buộc theo phạm vi lọc tour (destination/province) trước khi cho phép lưu.
  // * EN: Validate required fields based on selected tour scope (destination/province) before allowing save.
  function validateTourScope() {
    if (!basicInfo.tenantDestinationId) {
      showToast.error(
        t('tour.tenantDestinationRequired', {
          defaultValue: 'Vui lòng chọn điểm đến cho lộ trình.',
        }),
      )
      return false
    }
    if (basicInfo.scopeType === 'province' && !basicInfo.provinceId) {
      showToast.error(
        t('tour.provinceRequired', { defaultValue: 'Vui lòng chọn tỉnh/thành phố cho lộ trình.' }),
      )
      return false
    }
    return true
  }

  const translationSnapshot = useMemo(
    () => buildTourTranslationSnapshot(translations),
    [translations],
  )
  const basicInfoSnapshot = useMemo(() => buildTourBasicInfoSnapshot(basicInfo), [basicInfo])
  const coordinatesSnapshot = useMemo(
    () => buildTourCoordinatesSnapshot(routeCoordinates),
    [routeCoordinates],
  )
  const mediaSnapshot = useMemo(
    () => buildTourMediaSnapshot(banner, thumbnail),
    [banner, thumbnail],
  )
  const isTranslationDirty = isEdit
    ? translationSnapshot !== initialTranslationsSnapshotRef.current
    : translations.some((item) => item.name.trim() || item.description.trim())
  const isBasicInfoDirty = isEdit
    ? basicInfoSnapshot !== initialBasicInfoSnapshotRef.current
    : basicInfoSnapshot !== buildTourBasicInfoSnapshot(DEFAULT_BASIC_INFO) ||
      selectedPois.length > 0
  const isCoordinatesDirty = isEdit
    ? coordinatesSnapshot !== initialCoordinatesSnapshotRef.current
    : coordinatesSnapshot !== buildTourCoordinatesSnapshot([])
  const isMediaDirty = isEdit
    ? mediaSnapshot !== initialMediaSnapshotRef.current
    : (banner ?? '').length > 0 || (thumbnail ?? '').length > 0
  const isStatusDirty = !isEdit && !isActive
  const hasUnsavedChanges =
    isTranslationDirty || isBasicInfoDirty || isCoordinatesDirty || isMediaDirty || isStatusDirty
  const { dialog: unsavedChangesDialog, allowNextNavigation } =
    useUnsavedChangesGuard(hasUnsavedChanges)

  function handleCreateDestinationNavigation() {
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : scopedAdminPath(generatePath('/tenant/:tenantId/tours', { tenantId }))
    const createDestinationPath = `${scopedAdminPath(generatePath('/tenant/:tenantId/destinations', { tenantId }))}?returnTo=${encodeURIComponent(returnTo)}`
    allowNextNavigation()
    void navigate({ to: createDestinationPath as never })
  }

  // * VI: Luồng lưu tổng cho create/edit: validate toàn bộ form, build payload chuẩn API, gọi mutation và điều hướng về list khi thành công.
  // * EN: Main save flow for create/edit: validate full form, build API payload, run mutation, then navigate back to list on success.
  async function handleSubmit() {
    if (isThumbnailUploading || isBannerUploading) {
      showToast.error(t('tour.waitMediaUpload'))
      return
    }

    const normalizedLocalized = buildNormalizedTranslations()
    if (!hasRequiredTranslations(normalizedLocalized, ['name', 'description'])) {
      showToast.error(t('tour.allLanguagesTranslationRequired'))
      return
    }

    const trimmedBanner = banner?.trim() ?? ''
    const trimmedThumbnail = thumbnail?.trim() ?? ''
    if (!trimmedBanner || !trimmedThumbnail) {
      showToast.error(t('tour.imagesRequired'))
      return
    }

    if (basicInfo.duration < 30) {
      showToast.error(t('tour.durationMin30'))
      return
    }

    const trimmedTicketLink = basicInfo.ticketLink.trim()
    if (trimmedTicketLink && !/^https?:\/\/[^\s/$.?#].\S*$/i.test(trimmedTicketLink)) {
      showToast.error(
        t('tour.ticketLinkInvalid', {
          defaultValue: 'Link mua vé không hợp lệ. Phải bắt đầu bằng http:// hoặc https://',
        }),
      )
      return
    }

    if (!validateTourScope()) {
      return
    }

    const coordinates = buildCoordinatesForPayload()
    if (coordinates.length < 2) {
      showToast.error(t('tour.coordinatesNeedTwoPoints'))
      return
    }

    const poiIds = selectedPois.map((item) => item.poiId)

    const payload = {
      tenantDestinationId: basicInfo.tenantDestinationId,
      localizedData: normalizedLocalized,
      poiIds,
      duration: basicInfo.duration,
      coordinates,
      banner: trimmedBanner,
      thumbnail: trimmedThumbnail,
      ticketLink: basicInfo.ticketLink.trim() || null,
      isFavorite: basicInfo.isFavorite,
      ...(!isEdit ? { isActive } : {}),
    }

    try {
      if (isEdit && tourId) {
        const { poiIds: _poiIds, ...rest } = payload
        await updateMutation.mutateAsync({
          id: tourId,
          payload: rest,
        })
        clearCurrentJobs()
        showToast.success(t('tour.updateSuccess'))
      } else {
        await createMutation.mutateAsync(payload)
        clearCurrentJobs()
        showToast.success(t('tour.createSuccess'))
      }
      allowNextNavigation()
      await navigate({
        to: scopedAdminPath(generatePath('/tenant/:tenantId/tours', { tenantId })) as never,
      })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const isPoiActionPending =
    addTourPoisMutation.isPending ||
    removeTourPoisMutation.isPending ||
    updateTourPoiOrderMutation.isPending ||
    updateTourPoisBatchMutation.isPending

  const poisSnapshot = JSON.stringify(
    selectedPois
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((p) => ({
        poiId: p.poiId,
        color: p.color ?? null,
        favoritePoiRank: p.favoritePoiRank ?? null,
      })),
  )
  const isPoisBatchDirty = isEdit ? poisSnapshot !== initialPoisSnapshotRef.current : false

  function handleReorderMove(next: ITourPoi[]) {
    setFormState((prev) => ({ ...prev, selectedPois: next }))
  }

  // Chỉ update local state — mọi thay đổi POI (add/remove/color/rank/reorder) đều defer đến nút "Lưu thay đổi POI".
  function handleRemoveSelectedPois(next: ITourPoi[]) {
    setFormState((prev) => ({ ...prev, selectedPois: next }))
  }

  function addPoi(poi: IPOI) {
    if (selectedPois.some((item) => item.poiId === poi.id)) return
    setFormState((prev) => ({
      ...prev,
      selectedPois: [
        ...prev.selectedPois,
        { poiId: poi.id, poi, order: prev.selectedPois.length + 1 },
      ],
    }))
  }

  function addPoisMany(poisToAdd: IPOI[]) {
    const toAdd = poisToAdd.filter((poi) => !selectedPois.some((item) => item.poiId === poi.id))
    if (toAdd.length === 0) return
    setFormState((prev) => {
      const existing = new Set(prev.selectedPois.map((item) => item.poiId))
      const next = [...prev.selectedPois]
      toAdd.forEach((poi) => {
        if (!existing.has(poi.id)) {
          next.push({ poiId: poi.id, poi, order: next.length + 1 })
          existing.add(poi.id)
        }
      })
      return { ...prev, selectedPois: next }
    })
  }

  // Lưu toàn bộ thay đổi POI trong một lần: add mới, remove bị xóa, update color/rank, reorder.
  async function handleSavePoisBatch() {
    if (!isEdit || !tourId) return

    const serverPoiIds = new Set(serverPoisRef.current.map((p) => p.poiId))
    const currentPoiIds = new Set(selectedPois.map((p) => p.poiId))
    const serverPoiMap = new Map(serverPoisRef.current.map((p) => [p.poiId, p]))

    const toAdd = selectedPois.filter((p) => !serverPoiIds.has(p.poiId))
    const toRemove = serverPoisRef.current.filter((p) => !currentPoiIds.has(p.poiId))

    // Chỉ PATCH những POI thực sự thay đổi color/rank
    const toUpdate = selectedPois.filter((p) => {
      const server = serverPoiMap.get(p.poiId)
      if (!server) {
        // POI mới: chỉ cần update nếu color khác default hoặc có rank
        return (
          (p.color !== null && p.color !== undefined && p.color !== DEFAULT_CSS_COLOR_NAME) ||
          (p.favoritePoiRank !== null && p.favoritePoiRank !== undefined)
        )
      }
      return (
        (p.color ?? null) !== (server.color ?? null) ||
        (p.favoritePoiRank ?? null) !== (server.favoritePoiRank ?? null)
      )
    })

    try {
      if (toRemove.length > 0) {
        await removeTourPoisMutation.mutateAsync({
          id: tourId,
          poiIds: toRemove.map((p) => p.poiId),
        })
      }
      if (toAdd.length > 0) {
        await addTourPoisMutation.mutateAsync({ id: tourId, poiIds: toAdd.map((p) => p.poiId) })
      }
      if (toUpdate.length > 0) {
        await updateTourPoisBatchMutation.mutateAsync({ id: tourId, pois: toUpdate })
      }
      // Lưu thứ tự
      if (selectedPois.length > 0) {
        await updateTourPoiOrderMutation.mutateAsync({ id: tourId, pois: selectedPois })
      }

      serverPoisRef.current = [...selectedPois]
      initialPoisSnapshotRef.current = JSON.stringify(
        selectedPois
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((p) => ({
            poiId: p.poiId,
            color: p.color ?? null,
            favoritePoiRank: p.favoritePoiRank ?? null,
          })),
      )
      showToast.success(t('common.saved', { defaultValue: 'Đã lưu.' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  // * VI: Hàm lưu từng section ở edit mode với xử lý loading + toast dùng chung.
  // * EN: Shared edit-mode section saver with common loading and toast handling.
  async function saveEditSection(
    section: EditSection,
    payload: UpdateTourPayload,
  ): Promise<boolean> {
    if (!isEdit || !tourId) {
      return false
    }
    try {
      setSavingSection(section)
      await updateMutation.mutateAsync({ id: tourId, payload })
      showToast.success(t('common.saved', { defaultValue: 'Đã lưu.' }))
      return true
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return false
    } finally {
      setSavingSection(null)
    }
  }

  // * VI: Lưu riêng section translations, chỉ cập nhật snapshot khi API thành công để dirty-check luôn chính xác.
  // * EN: Save translations section only; update baseline snapshot only after successful API call for accurate dirty-check.
  async function handleSaveTranslations() {
    const normalizedLocalized = buildNormalizedTranslations()
    if (!hasRequiredTranslations(normalizedLocalized, ['name', 'description'])) {
      showToast.error(t('tour.allLanguagesTranslationRequired'))
      return
    }
    const success = await saveEditSection('translations', { localizedData: normalizedLocalized })
    if (success) {
      initialTranslationsSnapshotRef.current = translationSnapshot
      setHasPendingTranslationGeneration(false)
    }
  }

  // * VI: Lưu riêng section basic info (duration + destination), không đụng tọa độ route.
  // * EN: Save basic-info section (duration + destination) without touching route coordinates.
  async function handleSaveBasicInfo() {
    if (!validateTourScope()) {
      return
    }

    const trimmedTicketLink = basicInfo.ticketLink.trim()
    if (trimmedTicketLink && !/^https?:\/\/[^\s/$.?#].\S*$/i.test(trimmedTicketLink)) {
      showToast.error(
        t('tour.ticketLinkInvalid', {
          defaultValue: 'Link mua vé không hợp lệ. Phải bắt đầu bằng http:// hoặc https://',
        }),
      )
      return
    }

    const success = await saveEditSection('basicInfo', {
      duration: basicInfo.duration,
      tenantDestinationId: basicInfo.tenantDestinationId,
      ticketLink: basicInfo.ticketLink.trim() || null,
      isFavorite: basicInfo.isFavorite,
    })
    if (success) {
      initialBasicInfoSnapshotRef.current = basicInfoSnapshot
    }
  }

  // * VI: Lưu riêng tọa độ route để admin có thể cập nhật tuyến mà không lưu lại các trường khác.
  // * EN: Save route coordinates separately so admins can update the route without resubmitting other fields.
  async function handleSaveCoordinates() {
    const coordinates = buildCoordinatesForPayload()
    if (coordinates.length < 2) {
      showToast.error(t('tour.coordinatesNeedTwoPoints'))
      return
    }

    const success = await saveEditSection('coordinates', { coordinates })
    if (success) {
      const normalizedCoordinates = normalizeTourCoordinates(coordinates)
      initialCoordinatesSnapshotRef.current = buildTourCoordinatesSnapshot(normalizedCoordinates)
      setFormState((prev) => ({ ...prev, routeCoordinates: normalizedCoordinates }))
    }
  }

  // * VI: Lưu riêng media (banner/thumbnail), chặn lưu khi upload đang chạy hoặc thiếu ảnh bắt buộc.
  // * EN: Save media section (banner/thumbnail), blocking save while uploads are running or required images are missing.
  async function handleSaveMedia() {
    if (!isEdit || !tourId) {
      return
    }
    if (isThumbnailUploading || isBannerUploading) {
      showToast.error(t('tour.waitMediaUpload'))
      return
    }
    const trimmedBanner = banner?.trim() ?? ''
    const trimmedThumbnail = thumbnail?.trim() ?? ''
    if (!trimmedBanner || !trimmedThumbnail) {
      showToast.error(t('tour.imagesRequired'))
      return
    }
    try {
      setSavingSection('media')
      await updateMutation.mutateAsync({
        id: tourId,
        payload: {
          ...(thumbnail !== undefined ? { thumbnail } : {}),
          ...(banner !== undefined ? { banner } : {}),
        },
      })
      showToast.success(t('common.saved', { defaultValue: 'Đã lưu.' }))
      initialMediaSnapshotRef.current = mediaSnapshot
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setSavingSection(null)
    }
  }

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    isThumbnailUploading ||
    isBannerUploading

  return (
    <PageContainer>
      <PageHeader
        title={isEdit ? t('tour.edit') : t('tour.create')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantId
            ? [
                {
                  label: tenantName,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          {
            label: t('sidebar.tours'),
            path: scopedAdminPath(generatePath('/tenant/:tenantId/tours', { tenantId })),
          },
          { label: isEdit ? t('tour.edit') : t('tour.create') },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                void navigate({
                  to: scopedAdminPath(
                    generatePath('/tenant/:tenantId/tours', { tenantId }),
                  ) as never,
                })
              }}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common.back')}
            </Button>
            {!isEdit && (
              <Button disabled={isSaving || !hasUnsavedChanges} onClick={() => void handleSubmit()}>
                {isSaving ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1.5 h-4 w-4" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
          <div className="mb-4 flex items-center justify-between gap-3">
            <SectionHeader title={t('tour.translations')} />
            {isEdit && (
              <Button
                size="sm"
                disabled={updateMutation.isPending}
                onClick={handleSaveTranslations}
              >
                {savingSection === 'translations' && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
          {!isFormReady ? (
            <div className="space-y-4 rounded-xl border border-zinc-200 p-4">
              <div className="flex gap-2">
                <Skeleton className="h-7 w-14 rounded-full" />
                <Skeleton className="h-7 w-14 rounded-full" />
                <Skeleton className="h-7 w-14 rounded-full" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full rounded-md" />
                </div>
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-[124px] w-full rounded-md" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {pendingSuccessJobs.length > 0 && (
                <p className="mb-3 text-sm text-blue-700">
                  {t('jobs.runningCount', {
                    count: pendingSuccessJobs.length,
                  })}
                </p>
              )}
              <TourTranslationSection
                languages={languages}
                translations={translations}
                onChange={(value) => setFormState((prev) => ({ ...prev, translations: value }))}
                onGenerationStateChange={setHasPendingTranslationGeneration}
                entityId={translationEntityId}
                entityLabel={translations.find((item) => item.name.trim())?.name ?? t('tour.title')}
                {...(typeof window !== 'undefined' ? { entityPath: window.location.pathname } : {})}
              />
            </>
          )}
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(360px,0.95fr)]">
          <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
            <div className="mb-4 flex items-center justify-between gap-3">
              <SectionHeader title={t('tour.pois')} />
              {isEdit && (
                <Button
                  size="sm"
                  disabled={updateTourPoisBatchMutation.isPending || !isPoisBatchDirty}
                  onClick={() => void handleSavePoisBatch()}
                >
                  {updateTourPoisBatchMutation.isPending && (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  )}
                  {t('tour.savePoisChanges', { defaultValue: 'Lưu thay đổi POI' })}
                </Button>
              )}
            </div>
            <div className={cn('space-y-5', !isFormReady && 'opacity-50 pointer-events-none')}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    {t('tour.availablePois', { defaultValue: 'POI khả dụng' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-zinc-900">{pois.length}</p>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    {t('tour.selectedPois', { defaultValue: 'POI đã chọn' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-zinc-900">{selectedPois.length}</p>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    {t('tour.routePoints', { defaultValue: 'Điểm tuyến' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-zinc-900">
                    {routeCoordinates.length}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
                <div className="lg:col-span-6">
                  <p className="mb-3 text-sm font-semibold text-zinc-800">{t('tour.selectPois')}</p>
                  <TourPoiSelector
                    pois={pois}
                    selectedPoiIds={selectedPois.map((item) => item.poiId)}
                    disabled={!isFormReady || (isEdit && isPoiActionPending)}
                    isLoading={
                      Boolean(isPoisLoading) || Boolean(isPoisFetching && !isPoisFetchingNextPage)
                    }
                    isFetchingNextPage={isPoisFetchingNextPage}
                    hasNextPage={poisHasNextPage}
                    onLoadMore={() => void fetchNextPoisPage()}
                    languageOptions={poiLanguageOptions}
                    selectedLanguageCode={effectivePoiLanguageCode}
                    onLanguageChange={setSelectedPoiLanguageCode}
                    onQueryFiltersChange={setPoiQueryFilters}
                    onAdd={addPoi}
                    onAddMany={addPoisMany}
                  />
                </div>
                <div className="lg:col-span-6">
                  <p className="mb-3 text-sm font-semibold text-zinc-800">
                    {t('tour.selectedPois')}
                  </p>
                  <TourPoiOrderList
                    selectedPois={selectedPois}
                    poiMap={poiMap}
                    languageCode={effectivePoiLanguageCode}
                    onReorderMove={handleReorderMove}
                    onRemoveSelected={handleRemoveSelectedPois}
                    outOfScopePoiIds={outOfScopePoiIds}
                    isProcessing={!isFormReady || (isEdit && isPoiActionPending)}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-zinc-800">{t('tour.mapPreview')}</p>
                  {isEdit && (
                    <Button
                      size="sm"
                      disabled={updateMutation.isPending || !isCoordinatesDirty}
                      onClick={handleSaveCoordinates}
                    >
                      {savingSection === 'coordinates' && (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      )}
                      {t('common.save')}
                    </Button>
                  )}
                </div>
                <TourMapPreview
                  selectedPois={selectedPois}
                  poiMap={poiMap}
                  coordinates={routeCoordinates}
                  destinationAreaBoundary={selectedTenantDestination?.areaBoundary ?? null}
                  destinationCenterLat={selectedTenantDestination?.centerLat ?? null}
                  destinationCenterLng={selectedTenantDestination?.centerLng ?? null}
                  editable
                  onCoordinatesChange={(next) =>
                    setFormState((prev) => ({ ...prev, routeCoordinates: next }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
              <div className="mb-4 flex items-center justify-between gap-3">
                <SectionHeader title={t('tour.basicInfo')} />
                {isEdit && (
                  <Button
                    size="sm"
                    disabled={updateMutation.isPending || !isBasicInfoDirty}
                    onClick={handleSaveBasicInfo}
                  >
                    {savingSection === 'basicInfo' && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {t('common.save')}
                  </Button>
                )}
              </div>
              <div className={cn(!isFormReady && 'opacity-50 pointer-events-none')}>
                <TourBasicInfoForm
                  value={basicInfo}
                  countryOptions={countryOptions}
                  provinceOptions={provinceOptions}
                  tenantDestinationOptions={tenantDestinationOptions}
                  isProvinceLoading={isProvincesLoading}
                  isTenantDestinationLoading={isTenantDestinationsLoading}
                  onCreateDestination={handleCreateDestinationNavigation}
                  onChange={(value) => {
                    setFormState((prev) => {
                      const nextValue: TourBasicInfoState = { ...value }
                      if (nextValue.tenantDestinationId) {
                        const matchedDestination = resolvedTenantDestinationItems.find(
                          (item) => item.id === nextValue.tenantDestinationId,
                        )
                        if (matchedDestination) {
                          nextValue.countryId = matchedDestination.countryId ?? nextValue.countryId
                          nextValue.provinceId =
                            matchedDestination.provinceId ?? nextValue.provinceId
                        }
                      }
                      return { ...prev, basicInfo: nextValue }
                    })
                  }}
                />
                {!isEdit && (
                  <div className="mt-6 flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/60 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{t('common.status')}</p>
                      <p className="text-xs text-zinc-500">
                        {isActive ? t('common.active') : t('common.saveAsDraft')}
                      </p>
                    </div>
                    <Switch checked={isActive} onCheckedChange={setIsActive} />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
              <div className="mb-4 flex items-center justify-between gap-3">
                <SectionHeader title={t('tour.images')} />
                {isEdit && (
                  <Button
                    size="sm"
                    disabled={
                      updateMutation.isPending ||
                      isThumbnailUploading ||
                      isBannerUploading ||
                      !isMediaDirty
                    }
                    onClick={handleSaveMedia}
                  >
                    {savingSection === 'media' && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {t('common.save')}
                  </Button>
                )}
              </div>
              <div className={cn('space-y-5', !isFormReady && 'opacity-50 pointer-events-none')}>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <ImageUploadField
                    label={t('tour.banner')}
                    required
                    value={banner ?? ''}
                    onChange={(url) => setFormState((prev) => ({ ...prev, banner: url }))}
                    onUploadSuccess={() => showToast.success(t('tour.imageUploadSuccess'))}
                    onUploadError={(message) =>
                      showToast.error(message || t('tour.imageUploadFailed'))
                    }
                    uploadFailedText={t('tour.imageUploadFailed')}
                    onUploadingChange={setIsBannerUploading}
                    onPreview={setMediaPreviewUrl}
                    buttonSize="sm"
                    previewWrapperClassName="min-h-0"
                    previewButtonClassName="group relative block w-full cursor-zoom-in rounded-lg border border-zinc-200 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
                    previewImageClassName="pointer-events-none h-28 w-full rounded-[inherit] object-cover transition group-hover:opacity-90"
                    texts={{
                      select: t('tour.selectImage'),
                      uploading: t('tour.uploadingImage'),
                      empty: '—',
                      previewAlt: t('tour.banner'),
                    }}
                  />
                  <ImageUploadField
                    label={t('tour.thumbnail')}
                    required
                    value={thumbnail ?? ''}
                    onChange={(url) => setFormState((prev) => ({ ...prev, thumbnail: url }))}
                    onUploadSuccess={() => showToast.success(t('tour.imageUploadSuccess'))}
                    onUploadError={(message) =>
                      showToast.error(message || t('tour.imageUploadFailed'))
                    }
                    uploadFailedText={t('tour.imageUploadFailed')}
                    onUploadingChange={setIsThumbnailUploading}
                    onPreview={setMediaPreviewUrl}
                    buttonSize="sm"
                    previewWrapperClassName="min-h-0"
                    previewButtonClassName="group relative block w-full cursor-zoom-in rounded-lg border border-zinc-200 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
                    previewImageClassName="pointer-events-none h-28 w-full rounded-[inherit] object-cover transition group-hover:opacity-90"
                    texts={{
                      select: t('tour.selectImage'),
                      uploading: t('tour.uploadingImage'),
                      empty: '—',
                      previewAlt: t('tour.thumbnail'),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={mediaPreviewUrl !== null}
        onOpenChange={(open) => {
          if (!open) setMediaPreviewUrl(null)
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-[min(90vw,56rem)] gap-0 overflow-hidden p-2 sm:p-4">
          <DialogTitle className="sr-only">{t('common.preview')}</DialogTitle>
          {mediaPreviewUrl !== null && (
            <img
              src={mediaPreviewUrl}
              alt=""
              className="max-h-[min(85vh,calc(90vh-4rem))] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
      {unsavedChangesDialog}
    </PageContainer>
  )
}
